import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.KeyboardEvent
import i18n.StringKey
import i18n.tr

/**
 * The app's own prompt / confirm / alert, replacing `window.prompt`,
 * `window.confirm` and `window.alert`.
 *
 * The browser's dialogs are drawn by the browser: chrome we do not control, a
 * "wiz3r.com says" heading announcing the page as an untrusted website, and
 * room for a single line of text — no room for a subtitle, a hint, or an error.
 * They are also modal to the whole tab, so an error could only be reported by
 * opening a *second* dialog after the first had closed, taking whatever the
 * user had typed off screen with it.
 *
 * These build the same overlay-and-card the app already uses for its editors,
 * so they inherit the theme and read as part of the product.
 */

/** Live handle to an open prompt, so an async submit can report back into it. */
class AppPromptHandle(
    private val overlay: HTMLElement,
    private val confirmBtn: HTMLElement,
    private val errorEl: HTMLElement,
) {
    fun close() {
        overlay.parentElement?.removeChild(overlay)
    }

    /** Show [message] under the field and re-enable the button. The dialog stays
     *  open: a rejected value is worth correcting, not retyping. */
    fun showError(message: String) {
        errorEl.textContent = message
        errorEl.style.display = "block"
        confirmBtn.removeAttribute("disabled")
    }

    fun setBusy(busy: Boolean) {
        if (busy) confirmBtn.setAttribute("disabled", "true") else confirmBtn.removeAttribute("disabled")
    }
}

private fun overlayAndCard(): Pair<HTMLElement, HTMLElement> {
    val t = WebTheme.current
    val overlay = document.createElement("div") as HTMLElement
    overlay.style.cssText =
        "position: fixed; inset: 0; background: rgba(0,0,0,0.7); z-index: 1000; " +
            "display: flex; align-items: center; justify-content: center;"
    val card = document.createElement("div") as HTMLElement
    card.style.cssText =
        "background: ${t.card}; border-radius: 16px; padding: 24px; max-width: 340px; " +
            "width: 90%; color: ${t.textPrimary};"
    overlay.appendChild(card)
    // Backdrop dismisses; a click inside the card must not.
    overlay.addEventListener("click", { e -> if (e.target == overlay) overlay.parentElement?.removeChild(overlay) })
    return overlay to card
}

private fun buttonRow(card: HTMLElement): HTMLElement {
    val row = document.createElement("div") as HTMLElement
    row.style.cssText = "display: flex; gap: 8px; justify-content: flex-end;"
    card.appendChild(row)
    return row
}

private fun secondaryButton(text: String): HTMLElement {
    val t = WebTheme.current
    val b = document.createElement("button") as HTMLElement
    b.textContent = text
    b.style.cssText =
        "padding: 8px 16px; background: ${t.border}; border: none; border-radius: 8px; " +
            "color: ${t.textSecondary}; cursor: pointer; font-size: 13px;"
    return b
}

private fun primaryButton(text: String, destructive: Boolean): HTMLElement {
    val t = WebTheme.current
    val b = document.createElement("button") as HTMLElement
    b.textContent = text
    val bg = if (destructive) t.expenseRed else t.accent
    val fg = if (destructive) "#fff" else if (t.isDark) "#000" else "#fff"
    b.style.cssText =
        "padding: 8px 16px; background: $bg; border: none; border-radius: 8px; " +
            "color: $fg; cursor: pointer; font-size: 13px; font-weight: 600;"
    return b
}

private fun addTitle(card: HTMLElement, title: String, subtitle: String?) {
    val t = WebTheme.current
    val h = document.createElement("div") as HTMLElement
    h.textContent = title
    h.style.cssText =
        "font-size: 16px; font-weight: 600; color: ${t.textPrimary}; " +
            "margin-bottom: ${if (subtitle == null) "16px" else "2px"}; word-break: break-word;"
    card.appendChild(h)
    subtitle?.takeIf { it.isNotBlank() }?.let {
        val s = document.createElement("div") as HTMLElement
        s.textContent = it
        s.style.cssText = "font-size: 11px; color: ${t.textSecondary}; margin-bottom: 16px;"
        card.appendChild(s)
    }
}

/**
 * Ask for a line of text.
 *
 * [title] is what the text is ABOUT — the account being renamed, say — not the
 * instruction; that is [label]'s job. Naming the subject is the thing
 * `window.prompt` could never do, and its absence is what made the browser
 * version ask about nothing in particular.
 *
 * [onSubmit] receives the trimmed value and a handle: call `close()` when the
 * work succeeds, or `showError(...)` to keep the dialog open with the user's
 * text intact.
 */
fun showAppPrompt(
    title: String,
    subtitle: String? = null,
    label: String? = null,
    initial: String = "",
    placeholder: String = "",
    hint: String? = null,
    confirmLabel: String = StringKey.ACTION_SAVE.tr(),
    allowEmpty: Boolean = false,
    onSubmit: (String, AppPromptHandle) -> Unit,
) {
    val t = WebTheme.current
    val (overlay, card) = overlayAndCard()
    addTitle(card, title, subtitle)

    label?.let {
        val l = document.createElement("div") as HTMLElement
        l.textContent = it
        l.style.cssText = "font-size: 12px; color: ${t.textSecondary}; margin-bottom: 4px;"
        card.appendChild(l)
    }

    val input = document.createElement("input") as HTMLInputElement
    input.value = initial
    input.placeholder = placeholder
    input.style.cssText =
        "width: 100%; padding: 10px 12px; background: ${t.background}; border: 1px solid ${t.border}; " +
            "border-radius: 8px; color: ${t.textPrimary}; font-size: 14px; " +
            "margin-bottom: ${if (hint == null) "16px" else "6px"}; box-sizing: border-box; outline: none;"
    card.appendChild(input)

    hint?.let {
        val h = document.createElement("div") as HTMLElement
        h.textContent = it
        h.style.cssText = "font-size: 11px; color: ${t.textSecondary}; margin-bottom: 16px;"
        card.appendChild(h)
    }

    val error = document.createElement("div") as HTMLElement
    error.style.cssText = "font-size: 12px; color: ${t.expenseRed}; margin-bottom: 12px; display: none;"
    card.appendChild(error)

    val row = buttonRow(card)
    val cancel = secondaryButton(StringKey.ACTION_CANCEL.tr())
    cancel.addEventListener("click", { overlay.parentElement?.removeChild(overlay) })
    row.appendChild(cancel)

    val confirm = primaryButton(confirmLabel, destructive = false)
    row.appendChild(confirm)

    val handle = AppPromptHandle(overlay, confirm, error)
    fun submit() {
        val value = input.value.trim()
        if (value.isEmpty() && !allowEmpty) return
        error.style.display = "none"
        handle.setBusy(true)
        onSubmit(value, handle)
    }

    confirm.addEventListener("click", { submit() })
    input.addEventListener("keydown", { e ->
        when ((e as KeyboardEvent).key) {
            "Enter" -> submit()
            "Escape" -> overlay.parentElement?.removeChild(overlay)
        }
    })

    document.body?.appendChild(overlay)
    input.focus()
    input.select()
}

/**
 * Ask for a yes/no. [onConfirm] runs only on confirm; dismissing does nothing.
 *
 * [destructive] paints the confirm button red — a delete should not look like a
 * save, which `window.confirm` had no way to express.
 */
fun showAppConfirm(
    title: String,
    message: String? = null,
    confirmLabel: String,
    destructive: Boolean = false,
    onConfirm: () -> Unit,
) {
    val t = WebTheme.current
    val (overlay, card) = overlayAndCard()
    addTitle(card, title, null)

    message?.let {
        val m = document.createElement("div") as HTMLElement
        m.textContent = it
        // pre-line: callers pass newline-separated lists (the merge confirmation
        // itemises the accounts), which a plain div would run together.
        m.style.cssText = "font-size: 13px; color: ${t.textSecondary}; margin-bottom: 20px; line-height: 1.4; white-space: pre-line;"
        card.appendChild(m)
    }

    val row = buttonRow(card)
    val cancel = secondaryButton(StringKey.ACTION_CANCEL.tr())
    cancel.addEventListener("click", { overlay.parentElement?.removeChild(overlay) })
    row.appendChild(cancel)

    val confirm = primaryButton(confirmLabel, destructive)
    confirm.addEventListener("click", {
        overlay.parentElement?.removeChild(overlay)
        onConfirm()
    })
    row.appendChild(confirm)

    document.addEventListener("keydown", { e ->
        if ((e as KeyboardEvent).key == "Escape") overlay.parentElement?.removeChild(overlay)
    })

    document.body?.appendChild(overlay)
    confirm.focus()
}

/**
 * Tell the user something, with nothing to decide.
 *
 * [message] first, [title] optional: most callers have only a sentence — an
 * error from the server, a result — and setting that in bold heading type
 * shouts it. With no title the sentence is simply the body.
 */
fun showAppAlert(message: String, title: String? = null) {
    val t = WebTheme.current
    val (overlay, card) = overlayAndCard()
    title?.let { addTitle(card, it, null) }

    message.takeIf { it.isNotBlank() }?.let {
        val m = document.createElement("div") as HTMLElement
        m.textContent = it
        // pre-line: callers pass newline-separated lists (the merge confirmation
        // itemises the accounts), which a plain div would run together.
        m.style.cssText = "font-size: 13px; color: ${t.textSecondary}; margin-bottom: 20px; line-height: 1.4; white-space: pre-line;"
        card.appendChild(m)
    }

    val row = buttonRow(card)
    val ok = primaryButton(StringKey.ACTION_CLOSE.tr(), destructive = false)
    ok.addEventListener("click", { overlay.parentElement?.removeChild(overlay) })
    row.appendChild(ok)

    document.body?.appendChild(overlay)
    ok.focus()
}
